import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import type { ElasticSearchResponse, Facet } from '../@types/ElasticSearchResponse';
import type { ElasticVehicle } from '../@types/ElasticVehicle';
import type { BaseAction } from '../@types/actions';
import * as http from '../Http';
import { buildGetRequest } from '../Http/requestTemplates';
import type QueryFilter from '../helpers/query-filter';
import * as promises from '../utils/promises';
import requestCache from '../utils/request-cache';

export const REQ_SEARCH = 'REQ/SEARCH';
export const RCV_SEARCH = 'RCV/SEARCH';
type REQ_SEARCH_TYPE = BaseAction<typeof REQ_SEARCH> & {
  searchQueryParams: string;
  storeFacets: boolean;
};
type RCV_SEARCH_TYPE = BaseAction<typeof RCV_SEARCH> & {
  response: ElasticSearchResponse<ElasticVehicle>;
  searchQueryParams: string;
  storeFacets: boolean;
};

export const REQ_SEARCH_FACETS = 'REQ/SEARCH_FACETS';
export const RCV_SEARCH_FACETS = 'RCV/SEARCH_FACETS';
type REQ_SEARCH_FACETS_TYPE = BaseAction<typeof REQ_SEARCH_FACETS>;
type RCV_SEARCH_FACETS_TYPE = BaseAction<typeof RCV_SEARCH_FACETS> & {
  response: ElasticSearchResponse<ElasticVehicle>;
};

export const REQ_CLEAR = 'REQ/SEARCH_CLEAR';
type REQ_CLEAR_TYPE = BaseAction<typeof REQ_CLEAR>;

export const REQUEST_FAILED = 'SEARCH/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action: typeof REQ_SEARCH | typeof REQ_SEARCH_FACETS;
};

export type SEARCH_ACTION =
  | REQ_SEARCH_TYPE
  | RCV_SEARCH_TYPE
  | REQ_SEARCH_FACETS_TYPE
  | RCV_SEARCH_FACETS_TYPE
  | REQ_CLEAR_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<SEARCH_ACTION>;

const getFilterQueryString = (queryFilter: QueryFilter) => {
  if (queryFilter.isEmpty()) return '';

  return `?filter=${queryFilter.toQueryStringFilter()}`;
};

let _cancelSearch = () => false;
export const search =
  (queryFilter: QueryFilter, storeFacets = true) =>
  async (dispatch: Dispatch) => {
    const key = `${REQ_SEARCH}/${queryFilter.toString()}`;
    if (requestCache.isPending(key)) {
      return;
    }
    requestCache.isPending(key, true);

    _cancelSearch();
    dispatch<SEARCH_ACTION>({
      type: REQ_SEARCH,
      searchQueryParams: queryFilter.toString(),
      storeFacets,
    });

    const { promise, cancel } = promises.cancelable(
      http.json<ElasticSearchResponse<ElasticVehicle>>(
        `/api/search${getFilterQueryString(queryFilter)}`,
        buildGetRequest({ scrapeProbabilityScore: true }),
      ),
    );
    _cancelSearch = cancel;

    try {
      const response = await promise;
      dispatch<SEARCH_ACTION>({
        type: RCV_SEARCH,
        response: response.response,
        searchQueryParams: queryFilter.toString(),
        storeFacets,
      });
    } catch (err) {
      const _isCancelled = (err as { isCancelled: promises.CancelablePromiseRejection })
        .isCancelled;
      if (!_isCancelled) {
        dispatch<SEARCH_ACTION>({
          type: REQUEST_FAILED,
          action: REQ_SEARCH,
        });
      }
    } finally {
      requestCache.isPending(key, false);
    }
  };

export const callbackSearch = async (
  queryFilter: QueryFilter,
  callback: (documents: ElasticVehicle[], numberOfHits: number, facets: Facet[] | null) => void,
) => {
  try {
    const response = await http.json<ElasticSearchResponse<ElasticVehicle>>(
      `/api/search${getFilterQueryString(queryFilter)}`,
      buildGetRequest({ scrapeProbabilityScore: false }),
    );
    http.validateResponse(response);
    callback(
      response.response.documentList.documents,
      response.response.documentList.numberOfHits,
      response.response.facets,
    );
  } catch (_e) {
    // ignore
  }
};

export const callbackSearchFull = async (
  queryFilter: QueryFilter,
  callback: (documents: ElasticSearchResponse<ElasticVehicle>) => void,
) => {
  try {
    const response = await http.json<ElasticSearchResponse<ElasticVehicle>>(
      `/api/search${getFilterQueryString(queryFilter)}`,
      buildGetRequest({ scrapeProbabilityScore: true }),
    );
    http.validateResponse(response);
    callback(response.response);
  } catch (_e) {
    // ignore
  }
};

export const facets = () => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_SEARCH_FACETS)) {
    return;
  }
  requestCache.isPending(REQ_SEARCH_FACETS, true);

  try {
    dispatch<SEARCH_ACTION>({ type: REQ_SEARCH_FACETS });

    const response = await http.json<ElasticSearchResponse<ElasticVehicle>>(
      '/api/facets',
      buildGetRequest(),
    );
    http.validateResponse(response);
    dispatch<SEARCH_ACTION>({
      type: RCV_SEARCH_FACETS,
      response: response.response,
    });
  } catch (_e) {
    dispatch<SEARCH_ACTION>({ type: REQUEST_FAILED, action: REQ_SEARCH_FACETS });
  }
  requestCache.isPending(REQ_SEARCH_FACETS, false);
};

export const clear = () => (dispatch: Dispatch) => dispatch<SEARCH_ACTION>({ type: REQ_CLEAR });
